/**
 * CampaignReachPanel — who the appeal is being put to.
 *
 * An appeal is open to every assembly by default, which is what a movement-wide
 * campaign usually means. This is where an owner narrows it: tick the assemblies
 * it is actually being taken to, and give each one its own goal if they are
 * being asked for different amounts. An assembly that already has pledges on the
 * appeal cannot be silently shut out — the server refuses, and says which.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Globe, ListChecks, Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import api, { getErrorMessage } from '@/lib/api';
import AssemblyLeaderboard from './AssemblyLeaderboard';

export const CampaignReachPanel = ({ campaignId, currency = 'USD' }) => {
  const qc = useQueryClient();
  const [mode, setMode] = useState('all');
  const [picked, setPicked] = useState(() => new Set());
  const [targets, setTargets] = useState({});
  const [allowLocal, setAllowLocal] = useState(true);
  const [q, setQ] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['pledge-scope', campaignId],
    queryFn: () => api.get(`/campaigns/${campaignId}/pledge-scope`).then((r) => r.data),
    enabled: !!campaignId,
  });

  useEffect(() => {
    if (!data) return;
    setMode(data.pledge_scope?.mode || 'all');
    setPicked(new Set(data.pledge_scope?.church_ids || []));
    setAllowLocal(data.pledge_scope?.allow_local_pledges !== false);
    setTargets(Object.fromEntries(
      Object.entries(data.assembly_targets || {}).map(([k, v]) => [k, String(v)])));
  }, [data]);

  const assemblies = data?.assemblies || [];
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return assemblies;
    return assemblies.filter((a) => `${a.name} ${a.where}`.toLowerCase().includes(needle));
  }, [assemblies, q]);

  const save = useMutation({
    mutationFn: () => api.put(`/campaigns/${campaignId}/pledge-scope`, {
      mode,
      church_ids: Array.from(picked),
      allow_local_pledges: allowLocal,
      assembly_targets: Object.fromEntries(
        Object.entries(targets)
          .filter(([id, v]) => Number(v) > 0 && (mode === 'all' || picked.has(id)))
          .map(([id, v]) => [id, Number(v)])),
    }),
    onSuccess: () => {
      toast.success(mode === 'all'
        ? 'This appeal is open to every assembly'
        : `This appeal goes to ${picked.size} assembl${picked.size === 1 ? 'y' : 'ies'}`);
      qc.invalidateQueries({ queryKey: ['pledge-scope', campaignId] });
      qc.invalidateQueries({ queryKey: ['assembly-leaderboard'] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save the reach')),
  });

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />Loading the assemblies…
      </p>
    );
  }

  return (
    <div className="space-y-5" data-testid="campaign-reach-panel">
      <div className="grid gap-2 sm:grid-cols-2">
        <button type="button" onClick={() => setMode('all')}
                data-testid="reach-mode-all"
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                  mode === 'all' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
          <Globe className="mt-0.5 h-5 w-5 text-amber-600" />
          <span>
            <span className="block text-sm font-semibold text-slate-900">Open to every assembly</span>
            <span className="block text-xs text-slate-500">
              Any assembly can file pledges towards this appeal
            </span>
          </span>
        </button>
        <button type="button" onClick={() => setMode('selected')}
                data-testid="reach-mode-selected"
                className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${
                  mode === 'selected' ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:bg-slate-50'}`}>
          <ListChecks className="mt-0.5 h-5 w-5 text-amber-600" />
          <span>
            <span className="block text-sm font-semibold text-slate-900">Only the assemblies I tick</span>
            <span className="block text-xs text-slate-500">
              Everyone else will not see this appeal on their pledge form
            </span>
          </span>
        </button>
      </div>

      <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
        <div>
          <Label className="text-sm">Let assemblies pledge to their own work too</Label>
          <p className="text-xs text-slate-500">
            Turn off only if every pledge taken must be towards a campaign
          </p>
        </div>
        <Switch checked={allowLocal} onCheckedChange={setAllowLocal}
                data-testid="reach-allow-local" />
      </div>

      <Separator />

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-800">
            {mode === 'all' ? 'Goals per assembly (optional)' : 'Assemblies taking part'}
            {mode === 'selected' ? (
              <Badge variant="outline" className="ml-2" data-testid="reach-count">
                {picked.size} ticked
              </Badge>
            ) : null}
          </p>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-xs"
                   placeholder="Find an assembly" data-testid="reach-search" />
          </div>
        </div>
        <p className="text-xs text-slate-500">
          A goal lets a small assembly that meets its own target stand above a larger
          one that has not. Leave it blank and the assembly is ranked on what it gives.
        </p>

        <div className="max-h-80 space-y-1 overflow-y-auto pr-1">
          {shown.map((a) => (
            <div key={a.id}
                 data-testid={`reach-assembly-${a.id}`}
                 className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
              {mode === 'selected' ? (
                <Checkbox checked={picked.has(a.id)} onCheckedChange={() => toggle(a.id)}
                          data-testid={`reach-tick-${a.id}`} />
              ) : null}
              <span className="min-w-[10rem] flex-1">
                <span className="block text-sm text-slate-900">{a.name}</span>
                <span className="block text-xs text-slate-500">
                  {a.where || '—'}{a.is_assembly ? '' : ` · ${a.kind.replace(/_/g, ' ')}`}
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">{currency}</span>
                <Input type="number" value={targets[a.id] ?? ''}
                       placeholder="goal"
                       disabled={mode === 'selected' && !picked.has(a.id)}
                       onChange={(e) => setTargets((t) => ({ ...t, [a.id]: e.target.value }))}
                       className="h-8 w-28 text-sm" data-testid={`reach-target-${a.id}`} />
              </span>
            </div>
          ))}
          {!shown.length ? (
            <p className="p-3 text-sm text-slate-500">No assemblies match that.</p>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending}
                data-testid="reach-save">
          {save.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
          Save reach
        </Button>
      </div>

      <Separator />
      <AssemblyLeaderboard campaignId={campaignId} variant="admin" />
    </div>
  );
};

export default CampaignReachPanel;
